import { useEffect, useState } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { getLayerByCustomId } from '@utils/map/helpers';

/**
 * Custom hook for managing OpenLayers vector layer lifecycle
 * Handles layer creation, style updates, visibility, and cleanup
 *
 * @param {Object} mapObject - OpenLayers map instance
 * @param {string} layerId - Unique identifier for the layer
 * @param {Object} options - Layer configuration options
 * @param {string} options.name - Display name for the layer
 * @param {number} options.zIndex - Layer z-index for stacking order
 * @param {boolean} options.visible - Initial visibility state
 * @param {Function} options.styleFunction - OpenLayers style function
 * @param {boolean} options.declutter - Whether to declutter overlapping features
 * @returns {Object} Layer instance and control functions
 */
export const useMapLayer = (mapObject, layerId, options = {}) => {
	const {
		name = 'Layer',
		zIndex = 10,
		visible = false,
		styleFunction = null,
		declutter = false,
	} = options;

	const [layer, setLayer] = useState(null);

	// Initialize or retrieve layer
	useEffect(() => {
		if (!mapObject) return;

		const existingLayer = getLayerByCustomId(mapObject, layerId);

		if (existingLayer) {
			// Update existing layer with new style (null clears the style)
			existingLayer.setStyle(styleFunction ?? null);
			existingLayer.setZIndex(zIndex);
			setLayer(existingLayer);
			return;
		}

		// Create new layer
		const layerInstance = new VectorLayer({
			name,
			zIndex,
			visible,
			source: new VectorSource({}),
			properties: { customLayerId: layerId },
			style: styleFunction,
			declutter,
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);

		// Cleanup on unmount
		return () => {
			if (layerInstance && mapObject) mapObject.removeLayer(layerInstance);
		};
	}, [mapObject, layerId, zIndex, name, declutter]);

	// Update style when styleFunction changes
	useEffect(() => {
		if (!layer) return;
		layer.setStyle(styleFunction ?? null);
	}, [layer, styleFunction]);

	// Update visibility
	useEffect(() => {
		if (layer) layer.setVisible(visible);
	}, [layer, visible]);

	return { layer };
};
